import Flag, { FlagGroup } from '@atlaskit/flag';
import Error from '@atlaskit/icon/glyph/error';
import React, { useCallback, useState } from 'react';
import { buildBaseUrlByAppIdAndEnvIdAndModuleType, CodeclouUrlParameter, encodeUriSafe } from '../../components/util/url-encoding-helper';
import { CustomfieldEditorContext, ErrorOrPayload, ResolverEntryPoint } from '../../generated-common';
import { detectBrowserLanguage, sanitizeLocale, t_stateless } from '../util/i18n-stateless';
import { formatDate } from '../util/timezonAwareDate';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { FieldEditorSortIcon, StarLineIcon } from './CustomIcons';
import Icon from '@atlaskit/icon';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import { LicenseInvalidPage } from '../../pages/LicenseInvalid-Page';
import { FlagErrorBodyWithInvokeKey } from '../../shared-components/stateless/FlagErrorBodyWithInvokeKey';
import { token } from '@atlaskit/tokens';

type FlagDataType = {
  created: number;
  description: React.ReactNode;
  icon: React.ReactNode;
  id: number;
  key: number;
  title: string;
};

export interface IGlobalContextInner {
  entrypoint: string;
  locale: string;
  timezone: string;
  formatDate: (utcDate: string) => string;
  jiraLocaleOrBrowserLocale: string;
  helpUrl: (uri: string) => string;
  isLicenseActive: boolean;
}
export interface IGlobalContext {
  t: (key: string) => string;
  invoke: (key: string, params: any) => Promise<any>;
  encodeUriSafe: (uri: string, params: CodeclouUrlParameter[], absoluteUrl: boolean) => string;
  handleRestError: (err: null | ErrorOrPayload<any>, invokeKey: string) => Promise<any>;
  globals: IGlobalContextInner;
}
const globalsContext = {
  t: (_key: string) => {
    return '';
  },
  tWithIcon: (_key: string) => {
    return <></>;
  },
  routerOpen: (uri: string) => Promise.resolve(),
  invoke: (key: string, params: any) => Promise.resolve(null),
  handleRestError: (err: null | ErrorOrPayload<any>, invokeKey: string) => Promise.resolve(null),
  encodeUriSafe: (uri: string, params: CodeclouUrlParameter[], absoluteUrl: boolean) => '',
  globals: {
    locale: '',
    timezone: '',
    entrypoint: ResolverEntryPoint.FIELD_EDITOR, // dummy
    formatDate: (_utcDate: string) => {
      return '';
    },
    jiraLocaleOrBrowserLocale: '',
    helpUrl: (uri: string) => {
      return '';
    },
    isLicenseActive: false,
  },
};

const GlobalContext = React.createContext(globalsContext);

const GlobalContextProvider = ({
  invoke,
  routerOpen,
  customfieldEditorContext,

  children,
}: {
  invoke: (key: string, params: any) => Promise<any>;
  routerOpen: (uri: string) => void;
  customfieldEditorContext: CustomfieldEditorContext;
  children: React.ReactNode;
}) => {
  const [flags, setFlags] = useState<Array<FlagDataType>>([]);
  const addFlag = (newFlag: FlagDataType) => {
    setFlags((current) => [newFlag, ...current]);
  };

  const dismissFlag = useCallback(
    (id: string | number) => {
      setFlags((current) => current.filter((flag) => flag.id !== id));
    },
    [setFlags]
  );

  globalsContext.encodeUriSafe = (uri: string, params: CodeclouUrlParameter[], absoluteUrl: boolean) => {
    // BASEURL DEPENDING ON CONTEXT!
    const generatedBaseUrl = buildBaseUrlByAppIdAndEnvIdAndModuleType(customfieldEditorContext.context);
    // inject baseURL https://SITE.atlassian.net => Since Custom UI is inside iframe this works :)
    // see
    const siteAtlassianNetBaseURL = document.location.ancestorOrigins ? document.location.ancestorOrigins[0] : '';
    return encodeUriSafe(absoluteUrl ? siteAtlassianNetBaseURL + generatedBaseUrl : null, uri, params);
  };
  globalsContext.routerOpen = (uri: string) => {
    routerOpen(uri);
    return Promise.resolve();
  };
  globalsContext.globals.isLicenseActive = customfieldEditorContext.isLicenseActive;
  globalsContext.globals.locale = customfieldEditorContext.myself.locale ? customfieldEditorContext.myself.locale : '???';
  globalsContext.globals.timezone = customfieldEditorContext.myself.timeZone ? customfieldEditorContext.myself.timeZone : '???';
  globalsContext.globals.entrypoint = customfieldEditorContext.entrypoint;
  globalsContext.globals.helpUrl = (uri: string) => {
    return 'https://codeclou.io/customfield-editor-for-jira/cloud' + uri;
  };
  // view.getContext().then((x) => console.log(x));
  // FIXME: We can get localId and extract envId and appId from context here too
  let lang: string;
  if (globalsContext.globals.locale && globalsContext.globals.locale !== '') {
    lang = sanitizeLocale(globalsContext.globals.locale);
  } else {
    lang = detectBrowserLanguage();
  }
  // FIXME: FORCE LANG DE
  // lang = 'de';
  globalsContext.t = (key: string) => {
    return t_stateless(lang, key);
  };
  globalsContext.tWithIcon = (key: string) => {
    const translated = t_stateless(lang, key);
    return (
      <>
        {translated.split(' ').map((textFragment, index) => (
          <span key={index}>
            {textFragment === '___ICON_EDIT___' && <EditFilledIcon size="small" label="edit" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
            {textFragment === '___ICON_SORT___' && <Icon glyph={FieldEditorSortIcon} label="sort" size="small" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
            {textFragment === '___ICON_DEFAULT___' && <Icon glyph={StarLineIcon} label="default" size="small" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
            {textFragment === '___ICON_USERGROUP___' && <PeopleIcon size="small" label="edit" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
            {!textFragment.startsWith('___ICON') && <>{textFragment}</>}{' '}
          </span>
        ))}
      </>
    );
  };

  globalsContext.globals.jiraLocaleOrBrowserLocale = lang;
  // actions
  globalsContext.globals.formatDate = (u: string) => {
    return formatDate(u, globalsContext.globals.timezone, globalsContext.globals.locale);
  };
  globalsContext.invoke = invoke;
  globalsContext.handleRestError = async (err: null | ErrorOrPayload<any>, invokeKey: string) => {
    if (err && err.hasError) {
      addFlag({
        created: Date.now(),
        description: <FlagErrorBodyWithInvokeKey invokeKey={invokeKey} errorMessage={err!.errorMessage ? err!.errorMessage : ''} />,
        icon: <Error label="Error" primaryColor={token('color.text.danger')} />,
        id: flags.length,
        key: flags.length,
        title: 'Error calling Jira API',
      });
    }
    return null;
  };

  return (
    <GlobalContext.Provider value={globalsContext}>
      <FlagGroup onDismissed={dismissFlag}>
        {flags.map((flag) => (
          <Flag
            actions={
              [
                /*{
                content: 'close',
                onClick: () => dismissFlag(flag.id),
              },*/
              ]
            }
            {...flag}
          />
        ))}
      </FlagGroup>
      {!globalsContext.globals.isLicenseActive ? <LicenseInvalidPage /> : <>{children}</>}
    </GlobalContext.Provider>
  );
};

const GlobalContextConsumer = GlobalContext.Consumer;

export { GlobalContext, GlobalContextProvider, GlobalContextConsumer };
